import React from 'react';
import { Wallet } from 'lucide-react';
import { useBulleAuSurvol } from '../BulleSurvol';

/**
 * Qui paie ces publicités, et qui en bénéficie.
 *
 * ## Pourquoi cette carte existe
 *
 * `beneficiaryPayers` est servi par Meta sur **toute** annonce européenne — relevé sur 231/231
 * des lignes mesurées — et n'était lu nulle part. C'est le seul champ DSA de l'échantillon qui
 * discrimine réellement : les trois autres valent `All` / `18-65` / `France` partout.
 *
 * Deux renseignements, de natures différentes :
 *
 * 1. **Un payeur différent du bénéficiaire** désigne un revendeur, une agence ou une filiale
 *    qui achète pour la marque. Un fait, qu'aucune autre colonne ne porte.
 * 2. ⚠️ **Un couple identique vaut d'être lu aussi** : la page « Liquitex Artist Materials »
 *    déclare « Colart » des deux côtés, c'est-à-dire le groupe qui possède la marque. Ne
 *    montrer que le premier cas perdrait ce renseignement — un nom de page ne dit pas à qui
 *    elle appartient.
 *
 * La carte n'est pas rendue quand la liste est vide : il n'y a alors rien à nommer, et l'absence
 * est déjà expliquée par la carte de largeur de ciblage, qui dit si la collecte a ramené du
 * ciblage publié ou non.
 */
const PayeursDsa = ({ payeurs }) => {
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  if (!payeurs || payeurs.length === 0) return null;

  return (
    <div style={{
      padding: '20px',
      borderRadius: '12px',
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb'
    }}>
      <h3 style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: '600',
        marginBottom: '4px'
      }}>
        <Wallet size={16} />
        Qui paie ces publicités
      </h3>
      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
        Déclaration obligatoire de l'annonceur (DSA)
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {payeurs.map(p => (
          <div
            key={`${p.payeur}-${p.beneficiaire}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              borderRadius: '8px',
              backgroundColor: p.tiers ? '#fffbeb' : '#f9fafb',
              border: `1px solid ${p.tiers ? '#fde68a' : '#f3f4f6'}`
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '500' }}>{p.payeur}</div>
              {/* ⚠️ Le bénéficiaire n'est affiché que s'il diffère : répéter le même nom deux
                  fois ferait chercher une différence qui n'existe pas. */}
              {p.tiers && (
                <div style={{ fontSize: '12px', color: '#92400e' }}>
                  paie pour {p.beneficiaire}
                </div>
              )}
            </div>

            <span
              className="ksi-survolable"
              onMouseEnter={survoler(
                p.tiers
                  ? "Le payeur n'est pas le bénéficiaire : un revendeur, une agence ou une "
                    + "filiale achète pour cette marque. C'est un renseignement qu'aucune autre "
                    + "colonne de la bibliothèque ne porte."
                  : "Payeur et bénéficiaire sont le même. Le nom déclaré peut malgré tout "
                    + "différer de celui de la page : c'est alors le groupe qui possède la marque."
              )}
              onMouseLeave={quitter}
              style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}
            >
              {p.creas} créa{p.creas > 1 ? 's' : ''}
            </span>
          </div>
        ))}
      </div>

      {bulle}
    </div>
  );
};

export default PayeursDsa;
